export const CACHED_PRINT_LOGO_KEY = 'da-print-logo';
export const CACHED_DIGITAL_SIGNATURE_KEY = 'da-perma';
export const DEFAULT_HEADER_LOGO_WIDTH = 400;
export const PRINT_HEADER_FILES_KEY = 'print_header_files';
export const PRINT_HEADER_KEY = 'print_header';
export const USE_DIGITAL_SIGNATURE_KEY = 'use_digital_signature';
export const DIGITAL_SIGNATURE_KEY = 'digital_signature';
export const USE_DIGITAL_CLINIC_PRINT_HEADER = 'use_digital_clinic_print_header';
export const PRINT_HEADER_PER_CLINIC_KEY = 'print_header_per_clinic';
export const CACHED_PRINT_HEADER_PER_CLINIC = 'print-header-per-clinic';
export const SHOW_ADMIN_VIRTUAL_TOUR = 1;
export const NOT_SHOW_ADMIN_VIRTUAL_TOUR = 0;
/** Flag `1` without tour progress older than this is treated as stale and auto-completed. */
export const ADMIN_VIRTUAL_TOUR_FRESH_WINDOW_DAYS = 14;

export const TOOTH_NUMBERING_SYSTEM_KEY = 'tooth_numbering_system';

/** Stored `tooth_numbering_system` / API enum values — use these instead of string literals. */
export const TOOTH_NUMBERING_FDI = 'fdi';
export const TOOTH_NUMBERING_UNIVERSAL = 'universal';
export const TOOTH_NUMBERING_PALMER = 'palmer';

export const DEFAULT_TOOTH_NUMBERING_SYSTEM = TOOTH_NUMBERING_FDI;
export const ALLOW_STAFF_PRINT = '1';

/**
 * @deprecated Superseded by PRINT_LETTERHEAD_MODE.
 *
 * Boolean predecessor: '1' repeated the clinic letterhead and the signature
 * block on every page. Still read — and only read — by
 * resolvePrintLetterheadMode(), so accounts that set it before the mode setting
 * existed keep their behaviour without a migration script. Nothing else should
 * reference it; new code asks for the mode.
 */
export const PRINT_REPEAT_LETTERHEAD = 'print_repeat_letterhead';
export const PRINT_REPEAT_LETTERHEAD_ON = '1';

/**
 * Where the clinic letterhead and the signature / PRC-S2-PTR block go in a
 * print or export.
 *
 * One setting rather than two booleans ("include?" plus "repeat?") because the
 * two are not independent: "remove the letterhead, but repeat it on every page"
 * is not a state the product has, and modelling it as two flags means every
 * consumer has to remember that the second one only applies when the first is
 * on. Three named values, no impossible combination.
 *
 * Page numbering is deliberately absent from this enum. `n/N` is drawn on every
 * page in all three modes, because it is the only thing that tells a patient
 * their 2-page certificate did not arrive as 1 page — an integrity control, not
 * a preference.
 */
export const PRINT_LETTERHEAD_MODE = 'print_letterhead_mode';

/** Letterhead on page 1 only; signature block once, at the end of the content. */
export const PRINT_LETTERHEAD_FIRST_PAGE = 'first_page';
/** Letterhead and signature block repeated on every page. */
export const PRINT_LETTERHEAD_EVERY_PAGE = 'every_page';
/**
 * Neither drawn — originally for clinics printing onto pre-printed letterhead
 * stationery. Not offered in the export dialog any more; kept so the renderers
 * and resolvePrintLetterheadMode() still honour any account whose
 * `print_letterhead_mode` was set to this value via the settings API.
 */
export const PRINT_LETTERHEAD_NONE = 'none';

export type PrintLetterheadMode =
  | typeof PRINT_LETTERHEAD_FIRST_PAGE
  | typeof PRINT_LETTERHEAD_EVERY_PAGE
  | typeof PRINT_LETTERHEAD_NONE;

export const DEFAULT_PRINT_LETTERHEAD_MODE: PrintLetterheadMode = PRINT_LETTERHEAD_FIRST_PAGE;

/**
 * Option list for the export dialog and the settings form, so both read the
 * same labels. PRINT_LETTERHEAD_NONE is deliberately absent: the product no
 * longer offers "no letterhead" as a user-facing choice, though the mode itself
 * is still rendered correctly for accounts that already carry it.
 */
export const PRINT_LETTERHEAD_MODE_CHOICES: { value: PrintLetterheadMode; label: string }[] = [
  { value: PRINT_LETTERHEAD_FIRST_PAGE, label: 'Letterhead on the first page only' },
  { value: PRINT_LETTERHEAD_EVERY_PAGE, label: 'Letterhead on every page' }
];

/**
 * The effective letterhead mode for an account.
 *
 * `print_letterhead_mode` wins whenever it holds a value this build recognises.
 * Anything else — unset, empty, or a value written by a newer build — falls back
 * to the deprecated boolean and then to the default, so an unknown string can
 * never produce an undefined mode downstream.
 *
 * Pure on purpose: both PDF renderers call it with their own settings lookups,
 * which is what keeps them agreeing on one answer.
 */
export function resolvePrintLetterheadMode(mode: any, legacyRepeat?: any): PrintLetterheadMode {
  if (
    mode === PRINT_LETTERHEAD_FIRST_PAGE ||
    mode === PRINT_LETTERHEAD_EVERY_PAGE ||
    mode === PRINT_LETTERHEAD_NONE
  ) {
    return mode;
  }
  if (legacyRepeat == PRINT_REPEAT_LETTERHEAD_ON) {
    return PRINT_LETTERHEAD_EVERY_PAGE;
  }
  return DEFAULT_PRINT_LETTERHEAD_MODE;
}
export const PRINT_TYPE_BILL = 'bill';
export const PRINT_TYPE_PRESCRIPTION = 'prescription';
export const PRINT_TYPE_CHART = 'chart';
export const PRINT_TYPE_CERTIFICATE = 'certificate';
export const PRINT_TYPE_TREATMENT_PLAN = 'treatment_plan';
export const DEVICE_ID_KEY = 'deviceId';
export const DEVICE_STATUS_KEY = 'isDeviceAuthorized';
export const APPOINTMENT_FILTER = 'appointment_filter';
export const PHYSICAL_DEVICE_ID_KEY = 'physical_device_id';
export const THEME_PREFERENCES_KEY = 'mmp-theme-preferences-v1';
export const CALENDAR_SHOW_ALL_APPOINTMENTS_KEY = 'show-all-appointment';
export const CALENDAR_FILTER_PANEL_VISIBILITY_KEY = 'filter-panel-visibility';

/** Per-clinic online booking flow: suffix with the clinic id, e.g. `booking_flow_version_10`. */
export const BOOKING_FLOW_VERSION_KEY_PREFIX = 'booking_flow_version_';

/** Stored `booking_flow_version_<clinicId>` values — use these instead of string literals. */
export const BOOKING_FLOW_CLASSIC = 'classic';
export const BOOKING_FLOW_SIMPLIFIED_V1 = 'simplified_v1';

export interface CachedFileType {
  filename: string;
  width?: number;
  height?: number;
}
